import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CaretRight, Plus, TreeStructure, Trash } from 'phosphor-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { IDataNoPagination } from '../../types/shared';
import { IDepartamento, IDepartamentoWithUserRaw } from '../../hooks/useDepartamentos';
import { useDialog } from '../../providers/dialog';
import { iniciais } from './acessoApi';
import {
  AZUL,
  AZUL_ESCURO,
  Busca,
  Cartao,
  Contador,
  Etiqueta,
  Eyebrow,
  Interruptor,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';

interface IPessoa {
  id: number;
  nome: string;
  email: string;
}

interface IDepartamentoComPessoas extends IDepartamento {
  pessoas: IPessoa[];
}

type Situacao = 'todos' | 'ativos' | 'inativos';

const departamentosApi = {
  /** A rota corta por linha (departamento × pessoa), não por departamento: pede o teto. */
  comPessoas: async () => {
    const res = await api.get<IDataNoPagination<IDepartamentoWithUserRaw[]>>(
      '/api/v1/departamentos/with-users/',
      { params: { limit: 1000 } },
    );
    const porId = new Map<number, IDepartamentoComPessoas>();
    res.data.data.forEach(l => {
      if (!porId.has(l.id))
        porId.set(l.id, { id: l.id, nome: l.nome, status: l.status, pessoas: [] });
      if (l.user_id !== null)
        porId
          .get(l.id)!
          .pessoas.push({ id: l.user_id, nome: l.user_name || '', email: l.email || '' });
    });
    return Array.from(porId.values()).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  },
  salvar: async (d: { id?: number; nome: string; status: string }) => {
    const res = d.id
      ? await api.put<IDataNoPagination<IDepartamento>>(`/api/v1/departamentos/${d.id}/`, d)
      : await api.post<IDataNoPagination<IDepartamento>>('/api/v1/departamentos/', d);
    return res.data.data;
  },
  remover: async (id: number) => {
    await api.delete(`/api/v1/departamentos/${id}/`);
  },
};

const erroDaApi = (e: any, padrao: string) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return padrao;
};

function EditorDeDepartamento({
  departamento,
  onSalvo,
  onFechar,
}: {
  departamento: IDepartamentoComPessoas | null;
  onSalvo: (id: number) => void;
  onFechar: () => void;
}) {
  const navigate = useNavigate();
  const showDialog = useDialog();
  const queryClient = useQueryClient();
  const novo = !departamento;
  const [nome, setNome] = useState(departamento?.nome ?? '');
  const [ativo, setAtivo] = useState(departamento ? departamento.status === 'ativo' : true);
  const [buscaPessoa, setBuscaPessoa] = useState('');
  useEffect(() => {
    setNome(departamento?.nome ?? '');
    setAtivo(departamento ? departamento.status === 'ativo' : true);
  }, [departamento]);

  const nomeLimpo = nome.trim();
  const mudou =
    novo || nomeLimpo !== departamento.nome || ativo !== (departamento.status === 'ativo');
  const valido = nomeLimpo.length >= 2;

  const atualizar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['central', 'departamentos'] });
    // telas antigas e o formulário de usuário leem estas chaves
    queryClient.invalidateQueries({ queryKey: ['departments'] });
    queryClient.invalidateQueries({ queryKey: ['departmentsWithUsers'] });
  };

  const salvar = useMutation({
    mutationFn: () =>
      departamentosApi.salvar({
        id: departamento?.id,
        nome: nomeLimpo,
        status: ativo ? 'ativo' : 'inativo',
      }),
    onSuccess: async salvo => {
      await atualizar();
      toast.success(novo ? `Departamento ${nomeLimpo} criado` : 'Departamento salvo');
      onSalvo(salvo?.id ?? (departamento?.id as number));
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível salvar o departamento')),
  });
  const remover = useMutation({
    mutationFn: () => departamentosApi.remover(departamento!.id),
    onSuccess: async () => {
      await atualizar();
      toast.success('Departamento removido');
      onFechar();
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível remover o departamento')),
  });

  const pessoas = departamento?.pessoas ?? [];
  const termo = buscaPessoa.trim().toLowerCase();
  const pessoasVisiveis = pessoas.filter(
    p => !termo || `${p.nome} ${p.email}`.toLowerCase().includes(termo),
  );

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="16px">
        <Flex align="center" gap="14px" wrap="wrap">
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            bg="#DFF3FF"
            color={AZUL}
            flex="none"
          >
            <TreeStructure size={24} weight="duotone" />
          </Flex>
          <Box flex="1 1 240px" minW={0}>
            <Eyebrow>{novo ? 'NOVO DEPARTAMENTO' : 'DEPARTAMENTO'}</Eyebrow>
            <Text fontSize="22px" lineHeight="28px" fontWeight={700} color={TEXTO} noOfLines={1}>
              {novo ? nomeLimpo || 'Sem nome' : departamento.nome}
            </Text>
            {!novo && (
              <Text fontSize="13px" color="#475569">
                {pessoas.length} {pessoas.length === 1 ? 'pessoa' : 'pessoas'}
              </Text>
            )}
          </Box>
        </Flex>

        <Flex gap="16px" wrap="wrap" align="flex-end">
          <Box flex="1 1 260px">
            <Eyebrow>NOME</Eyebrow>
            <Input
              mt="4px"
              h="40px"
              borderRadius="10px"
              bg="white"
              borderColor="#CBD5E1"
              value={nome}
              aria-label="Nome do departamento"
              placeholder="Ex.: Fiscal, Contábil, RH"
              onChange={e => setNome(e.target.value)}
            />
            {nome.length > 0 && !valido && (
              <Text fontSize="12px" color="#991B1B" mt="4px">
                O nome precisa de pelo menos 2 letras.
              </Text>
            )}
          </Box>
          <Flex align="center" gap="10px" h="40px">
            <Interruptor
              ligado={ativo}
              onChange={() => setAtivo(v => !v)}
              rotulo="Departamento ativo"
            />
            <Text fontSize="13.5px" fontWeight={600} color={TEXTO}>
              {ativo ? 'Ativo' : 'Inativo'}
            </Text>
          </Flex>
        </Flex>
        {!ativo && !novo && departamento.status === 'ativo' && (
          <Text fontSize="12.5px" color="#92400E">
            Inativo, ele some da escolha de departamento no cadastro de usuário. Quem já está nele
            continua.
          </Text>
        )}

        <Flex gap="8px" justify="flex-end" wrap="wrap" pt="4px">
          {!novo && (
            <Button
              size="sm"
              variant="ghost"
              borderRadius="999px"
              color="#991B1B"
              leftIcon={<Trash size={14} />}
              mr="auto"
              isLoading={remover.isPending}
              onClick={() =>
                showDialog({
                  status: 'warning',
                  title: `Remover o departamento ${departamento.nome}?`,
                  description: pessoas.length
                    ? `${pessoas.length} ${
                        pessoas.length === 1 ? 'pessoa ainda está' : 'pessoas ainda estão'
                      } nele. Troque o departamento delas no cadastro antes, ou elas ficam sem departamento visível.`
                    : 'Ninguém está neste departamento.',
                  cancelLabel: 'Cancelar',
                  async onConfirm() {
                    await remover.mutateAsync();
                  },
                })
              }
            >
              Remover
            </Button>
          )}
          <Button size="sm" variant="ghost" borderRadius="999px" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            borderRadius="999px"
            color="white"
            bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
            _hover={{ bg: AZUL_ESCURO }}
            isDisabled={!valido || !mudou}
            isLoading={salvar.isPending}
            onClick={() => salvar.mutate()}
          >
            {novo ? 'Criar departamento' : 'Salvar'}
          </Button>
        </Flex>
      </Cartao>

      {!novo && (
        <Cartao p="18px 22px" display="flex" flexDirection="column" gap="10px">
          <Flex justify="space-between" align="center" gap="8px" wrap="wrap">
            <Eyebrow>QUEM ESTÁ NESTE DEPARTAMENTO</Eyebrow>
            <Contador>{pessoas.length}</Contador>
          </Flex>
          {pessoas.length > 8 && (
            <Box maxW="420px">
              <Busca valor={buscaPessoa} onChange={setBuscaPessoa} placeholder="Buscar pessoa" />
            </Box>
          )}
          {pessoas.length === 0 && (
            <Text fontSize="13px" color={TEXTO_SUAVE}>
              Ninguém ainda. O departamento de cada pessoa é escolhido no cadastro dela.
            </Text>
          )}
          <Flex direction="column" gap="2px">
            {pessoasVisiveis.map(p => (
              <Flex
                key={p.id}
                as="button"
                type="button"
                align="center"
                gap="10px"
                p="8px 10px"
                borderRadius="12px"
                textAlign="left"
                _hover={{ bg: 'rgba(255,255,255,.85)' }}
                onClick={() => navigate(`/configuracoes/permissoes?usuario=${p.id}`)}
              >
                <Flex
                  w="32px"
                  h="32px"
                  flex="none"
                  borderRadius="50%"
                  align="center"
                  justify="center"
                  fontSize="12px"
                  fontWeight={700}
                  color="#075985"
                  bg="#DFF3FF"
                >
                  {iniciais(p.nome || p.email)}
                </Flex>
                <Box flex={1} minW={0}>
                  <Text fontSize="13.5px" fontWeight={600} color={TEXTO} noOfLines={1}>
                    {p.nome || p.email}
                  </Text>
                  <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
                    {p.email}
                  </Text>
                </Box>
                <CaretRight size={14} color={TEXTO_SUAVE} />
              </Flex>
            ))}
          </Flex>
          {pessoas.length > 0 && (
            <Text fontSize="12px" color={TEXTO_SUAVE}>
              Para mudar alguém de departamento, abra a pessoa e use &quot;Editar cadastro&quot;.
            </Text>
          )}
        </Cartao>
      )}
    </Flex>
  );
}

export function DepartamentosCentral() {
  const [params, setParams] = useSearchParams();
  const selecionado = params.get('dep');
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<Situacao>('todos');

  const { data: departamentos = [], isLoading } = useQuery({
    queryKey: ['central', 'departamentos'],
    queryFn: departamentosApi.comPessoas,
  });

  const abrir = (dep: string | null) => {
    const p = new URLSearchParams(params);
    if (dep === null) p.delete('dep');
    else p.set('dep', dep);
    setParams(p, { replace: true });
  };

  const totais = useMemo(
    () => ({
      todos: departamentos.length,
      ativos: departamentos.filter(d => d.status === 'ativo').length,
      inativos: departamentos.filter(d => d.status !== 'ativo').length,
    }),
    [departamentos],
  );
  const termo = busca.trim().toLowerCase();
  const filtrados = departamentos.filter(d => {
    if (situacao === 'ativos' && d.status !== 'ativo') return false;
    if (situacao === 'inativos' && d.status === 'ativo') return false;
    return !termo || d.nome.toLowerCase().includes(termo);
  });
  const atual = departamentos.find(d => String(d.id) === selecionado) ?? null;
  const editando = selecionado === 'novo' || !!atual;

  return (
    <Flex gap="20px" align="flex-start" wrap="wrap">
      <Cartao
        as="section"
        aria-label="Departamentos"
        flex="1 1 280px"
        maxW={{ base: '100%', lg: '340px' }}
        minW={0}
        p="18px"
        display="flex"
        flexDirection="column"
        gap="12px"
      >
        <Flex align="center" justify="space-between" gap="8px">
          <Eyebrow>DEPARTAMENTOS</Eyebrow>
          <Button
            size="xs"
            borderRadius="999px"
            variant="outline"
            bg="white"
            leftIcon={<Plus size={12} />}
            onClick={() => abrir('novo')}
          >
            Novo departamento
          </Button>
        </Flex>
        <Busca valor={busca} onChange={setBusca} placeholder="Buscar departamento" />
        <Flex gap="6px" wrap="wrap">
          <Pilula
            ativa={situacao === 'todos'}
            onClick={() => setSituacao('todos')}
            contagem={totais.todos}
          >
            Todos
          </Pilula>
          <Pilula
            ativa={situacao === 'ativos'}
            onClick={() => setSituacao('ativos')}
            contagem={totais.ativos}
          >
            Ativos
          </Pilula>
          <Pilula
            ativa={situacao === 'inativos'}
            onClick={() => setSituacao('inativos')}
            contagem={totais.inativos}
          >
            Inativos
          </Pilula>
        </Flex>
        <Flex direction="column" gap="4px" maxH="560px" overflowY="auto" mx="-6px" px="6px">
          {isLoading && (
            <Flex justify="center" py={6}>
              <Spinner color={AZUL} />
            </Flex>
          )}
          {!isLoading && filtrados.length === 0 && (
            <Text fontSize="13px" color={TEXTO_SUAVE} py="8px">
              Nenhum departamento com esse filtro.
            </Text>
          )}
          {filtrados.map(d => {
            const aberto = String(d.id) === selecionado;
            const ativo = d.status === 'ativo';
            return (
              <Flex
                key={d.id}
                as="button"
                type="button"
                onClick={() => abrir(String(d.id))}
                aria-current={aberto ? 'true' : undefined}
                align="center"
                gap="10px"
                p="9px 10px"
                borderRadius="14px"
                textAlign="left"
                opacity={ativo ? 1 : 0.6}
                bg={aberto ? '#DFF3FF' : 'transparent'}
                boxShadow={aberto ? `inset 0 0 0 1px ${AZUL}33` : undefined}
                _hover={aberto ? undefined : { bg: 'rgba(255,255,255,.8)' }}
              >
                <Box flex={1} minW={0}>
                  <Text fontSize="13.5px" fontWeight={600} color={TEXTO} noOfLines={1}>
                    {d.nome}
                  </Text>
                  <Text fontSize="12px" color={TEXTO_SUAVE}>
                    {d.pessoas.length === 0
                      ? 'ninguém'
                      : `${d.pessoas.length} ${d.pessoas.length === 1 ? 'pessoa' : 'pessoas'}`}
                  </Text>
                </Box>
                {!ativo && <Etiqueta tom="neutro">inativo</Etiqueta>}
              </Flex>
            );
          })}
        </Flex>
      </Cartao>

      <Box flex="999 1 520px" minW={0}>
        {editando ? (
          <EditorDeDepartamento
            key={selecionado ?? ''}
            departamento={atual}
            onSalvo={id => abrir(String(id))}
            onFechar={() => abrir(null)}
          />
        ) : (
          <Cartao
            p="28px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="10px"
            textAlign="center"
          >
            <Flex
              w="52px"
              h="52px"
              borderRadius="16px"
              bg="#DFF3FF"
              color={AZUL}
              align="center"
              justify="center"
            >
              <TreeStructure size={26} weight="duotone" />
            </Flex>
            <Text fontSize="16px" fontWeight={700} color={TEXTO}>
              {isLoading && selecionado ? 'Carregando…' : 'Escolha um departamento'}
            </Text>
            <Text fontSize="13px" color={TEXTO_SUAVE} maxW="440px">
              Departamento organiza as pessoas por área. Telas e empresas vêm do grupo; no Kanban,
              quem é da Gestão administra os projetos do próprio departamento.
            </Text>
            <Button
              mt="6px"
              size="sm"
              borderRadius="999px"
              color="white"
              bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
              _hover={{ bg: AZUL_ESCURO }}
              leftIcon={<Plus size={14} />}
              onClick={() => abrir('novo')}
            >
              Novo departamento
            </Button>
          </Cartao>
        )}
      </Box>
    </Flex>
  );
}
